/**
 * The one config screen. Every config type renders through it — the route
 * carries a registry key and the schema supplies the rest.
 *
 * There is deliberately no `DebtorConsentPage` and no `MailboxPage`. When a
 * config type is added on the server, it appears here with a working list,
 * search, filters, editor, validation and history, and nobody ships a frontend
 * release to make that happen. That is the whole return on the schema-driven
 * design, so resist adding `if (type === ...)` here.
 */

import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useConfigList, useSchema } from '../api/hooks'
import type { ConfigRow, ConfigTypeSchema } from '../api/types'
import { CellValue } from '../fields/CellValue'
import { cellClassName } from '../fields/cellClassName'
import { RowEditor } from '../components/RowEditor'
import { Banner, EmptyState, ErrorBanner, Spinner } from '../components/feedback'

const PAGE_SIZE = 50

function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timer)
  }, [value, delay])
  return debounced
}

function ConfigTable({
  schema,
  rows,
  sort,
  dir,
  selectedKey,
  onSort,
  onSelect,
}: {
  schema: ConfigTypeSchema
  rows: ConfigRow[]
  sort: string
  dir: 'asc' | 'desc'
  selectedKey: string | null
  onSort: (field: string) => void
  onSelect: (row: ConfigRow) => void
}) {
  const fieldFor = (name: string) => schema.fields.find((f) => f.name === name)

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {schema.list_columns.map((name) => {
              const field = fieldFor(name)
              const label =
                name === schema.key_field.name ? schema.key_field.label : (field?.label ?? name)
              return (
                <th key={name} scope="col">
                  <button type="button" onClick={() => onSort(name)}>
                    {label}
                    {sort === name ? <span aria-hidden="true">{dir === 'asc' ? '↑' : '↓'}</span> : null}
                  </button>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = String(row[schema.key_field.name])
            return (
              <tr
                key={key}
                className={key === selectedKey ? 'is-selected' : undefined}
                onClick={() => onSelect(row)}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelect(row)
                  }
                }}
              >
                {schema.list_columns.map((name) => {
                  const isKey = name === schema.key_field.name
                  return (
                    <td key={name} className={cellClassName(fieldFor(name), isKey)}>
                      {isKey ? key : <CellValue field={fieldFor(name)} value={row[name]} />}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Outer shell: the screen is remounted whenever the config type or the deep
 * link changes.
 *
 * Keying it this way is what removes two state-syncing effects. Search terms,
 * filters and paging reset when you switch config type — which they must,
 * since a filter on a column the next table does not have would silently return
 * nothing — and arriving with `?row=` opens that row without an effect watching
 * for it.
 */
export function ConfigTypePage() {
  const { type = '' } = useParams()
  const [searchParams] = useSearchParams()
  return <ConfigScreen key={`${type}:${searchParams.get('row') ?? ''}`} type={type} />
}

function ConfigScreen({ type }: { type: string }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const schemaQuery = useSchema()

  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Record<string, string>>({})
  const [sortState, setSortState] = useState<{ field: string; dir: 'asc' | 'desc' } | null>(null)
  const [page, setPage] = useState(1)
  const [selection, setSelection] = useState<{ row: ConfigRow | null } | null>(null)

  const schema = schemaQuery.data?.config_types.find((t) => t.key === type)

  // Sort falls back to the registry default rather than being copied into state
  // on load, so there is no window where the header and the request disagree.
  const sort = sortState?.field ?? schema?.default_sort?.field ?? schema?.key_field.name ?? ''
  const dir = sortState?.dir ?? schema?.default_sort?.dir ?? 'asc'

  const debouncedSearch = useDebounced(search)

  const params = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      page,
      page_size: PAGE_SIZE,
      sort: sort || undefined,
      dir,
      filters,
    }),
    [debouncedSearch, page, sort, dir, filters],
  )

  const list = useConfigList(type, params, Boolean(schema))

  // Deep link: /config/debtor-consent?row=ACMECOZZZZ opens that row. Derived
  // from the list rather than pushed into state, so it simply resolves once the
  // rows arrive. An explicit click always wins over it.
  const deepLinkKey = searchParams.get('row')
  const deepLinkRow = useMemo(() => {
    if (!deepLinkKey || !schema || !list.data) return null
    return (
      list.data.rows.find((row) => String(row[schema.key_field.name]) === deepLinkKey) ?? null
    )
  }, [deepLinkKey, schema, list.data])

  const editing = selection ?? (deepLinkRow ? { row: deepLinkRow } : null)

  if (schemaQuery.isLoading) return <div className="page"><Spinner label="Loading configuration…" /></div>
  if (schemaQuery.error) return <div className="page"><ErrorBanner error={schemaQuery.error} /></div>

  if (!schema) {
    return (
      <div className="page">
        <Banner tone="warn" title="No such configuration screen">
          Either that config type does not exist, or you have not been granted access to
          it. The navigation only lists what your permissions cover.
        </Banner>
      </div>
    )
  }

  const handleSort = (field: string) => {
    setSortState({ field, dir: sort === field && dir === 'asc' ? 'desc' : 'asc' })
    setPage(1)
  }

  const closeEditor = () => {
    setSelection(null)
    if (deepLinkKey) {
      // Clearing the parameter also changes this screen's key, so the remount
      // is what actually closes the drawer in the deep-linked case.
      searchParams.delete('row')
      setSearchParams(searchParams, { replace: true })
    }
  }

  const total = list.data?.total ?? 0
  const rows = list.data?.rows ?? []
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const hasToolbarControls = schema.searchable || (schema.filters ?? []).length > 0

  return (
    <div className="page">
      <header className="page__header">
        <div className="page__title-row">
          <div>
            <h1 className="page__title">{schema.label}</h1>
            {schema.description ? <p className="page__description">{schema.description}</p> : null}
          </div>
          {schema.can_create && schema.permissions.write ? (
            <button type="button" className="btn btn--primary" onClick={() => setSelection({ row: null })}>
              Add {schema.label.toLowerCase().replace(/s$/, '')}
            </button>
          ) : null}
        </div>
      </header>

      {!schema.permissions.write ? (
        <Banner tone="info" title="Read-only">
          You can view {schema.label.toLowerCase()} but not change it. An admin can grant
          write access on the Access screen.
        </Banner>
      ) : null}

      <ErrorBanner error={list.error} context="Could not load this list." />

      <div className="card">
        <div className={`toolbar${hasToolbarControls ? '' : ' toolbar--bare'}`}>
          {schema.searchable ? (
            <>
              <label className="visually-hidden" htmlFor="config-search">
                Search {schema.label}
              </label>
              <input
                id="config-search"
                className="input input--search"
                type="search"
                placeholder={`Search ${schema.label.toLowerCase()}…`}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value)
                  setPage(1)
                }}
              />
            </>
          ) : null}

          {(schema.filters ?? []).map((filter) => (
            <div key={filter.name}>
              <label className="visually-hidden" htmlFor={`filter-${filter.name}`}>
                {filter.label}
              </label>
              <select
                id={`filter-${filter.name}`}
                className="select select--filter"
                value={filters[filter.name] ?? ''}
                onChange={(event) => {
                  const value = event.target.value
                  setFilters((current) => ({ ...current, [filter.name]: value }))
                  setPage(1)
                }}
              >
                <option value="">{filter.label}: any</option>
                {filter.kind === 'bool' ? (
                  <>
                    <option value="true">{filter.label}: on</option>
                    <option value="false">{filter.label}: off</option>
                  </>
                ) : (
                  (filter.options ?? []).map((option) => (
                    <option key={option.value} value={option.value}>
                      {filter.label}: {option.label}
                    </option>
                  ))
                )}
              </select>
            </div>
          ))}

          <span className="toolbar__spacer" />
          <span className="toolbar__count">
            {list.isFetching ? 'Loading…' : `${total} row${total === 1 ? '' : 's'}`}
          </span>
        </div>

        {list.isLoading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <EmptyState title="Nothing to show">
            {search || Object.values(filters).some(Boolean)
              ? 'No rows match the current search and filters.'
              : 'This table has no rows yet.'}
          </EmptyState>
        ) : (
          <ConfigTable
            schema={schema}
            rows={rows}
            sort={sort}
            dir={dir}
            selectedKey={editing?.row ? String(editing.row[schema.key_field.name]) : null}
            onSort={handleSort}
            onSelect={(row) => setSelection({ row })}
          />
        )}

        {pageCount > 1 ? (
          <div className="toolbar" style={{ borderBottom: 0, borderTop: '1px solid var(--border)' }}>
            <button
              type="button"
              className="btn btn--sm"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </button>
            <span className="toolbar__count">
              Page {page} of {pageCount}
            </span>
            <button
              type="button"
              className="btn btn--sm"
              disabled={page >= pageCount}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      {editing ? (
        <RowEditor
          key={editing.row ? String(editing.row[schema.key_field.name]) : '__new__'}
          schema={schema}
          row={editing.row}
          onClose={closeEditor}
        />
      ) : null}
    </div>
  )
}
